import React, { useEffect } from 'react';
import ProtectedRoute from './ProtectedRoute';
import ResponsiveLayout from '../layout/ResponsiveLayout';
import { NotificationProvider } from '../../contexts/NotificationContext';
import { useDbUser } from '../../hooks/useDbUser';
import { TimerCheckWrapper } from './TimerCheckWrapper';
import { db } from '../../lib/db';

// Props interface for the ProtectedRouteWithLayout component
// children: The page content that needs authentication and app layout
interface ProtectedRouteWithLayoutProps {
  children: React.ReactNode;
}

/**
 * ProtectedRouteWithLayout Component
 * 
 * This is a wrapper component that combines authentication protection with the full app layout.
 * It's like putting a security guard (ProtectedRoute) at the entrance of a building that has 
 * multiple floors and services (Layout, Timer, Notifications).
 * 
 * What it provides:
 * 1. Authentication protection (must be logged in)
 * 2. App layout (navigation, responsive design)
 * 3. Timer functionality (for tracking work time)
 * 4. Notification system (for deadlines and reminders)
 * 5. Database user initialization
 * 6. Cleanup of old deferred tasks when user logs in
 * 
 * @param children - The main page content to display
 */
function ProtectedRouteWithLayout({ children }: ProtectedRouteWithLayoutProps) {
  // Get the user's database ID (different from their login ID)
  const { dbUserId } = useDbUser();

  // When the user logs in or navigates to the app, clean up old deferred tasks
  // Deferred tasks are tasks marked as "do tomorrow" that should become active again the next day
  useEffect(() => {
    if (dbUserId) {
      // Look for tasks that were deferred to "tomorrow" but that tomorrow has now passed
      db.tasks.cleanupDeferredTasks(dbUserId).then(cleanedCount => {
        if (cleanedCount > 0) {
          console.log(`[Login] Cleaned up ${cleanedCount} deferred tasks from previous days`);
        }
      }).catch(err => {
        console.error('[Login] Error cleaning up deferred tasks:', err);
      });
    }
  }, [dbUserId]); // Run this effect when the user ID changes (i.e., when they log in)

  return (
    // First layer: Authentication protection - only logged-in users can proceed
    <ProtectedRoute>
      {/* Timer checker makes sure running timers redirect correctly */}
      <TimerCheckWrapper>
        {/* Notification system */}
        <NotificationProvider userId={dbUserId}>
          {/* App layout */}
          <ResponsiveLayout>
            {/* Page content */}
            {children}
          </ResponsiveLayout>
        </NotificationProvider>
      </TimerCheckWrapper>
    </ProtectedRoute>
  );
}

export default ProtectedRouteWithLayout;
